import { useState } from 'react';
import { useDebounce } from 'react-use';
import { useField } from 'formik';
import { isEqual } from 'lodash';
import styled, { useTheme } from 'styled-components';
import { FormField } from 'ui/forms';
import {
  Dropdown,
  PopoverContainer,
  PopoverMenu,
  Stack,
  PopoverItemButton as RawPopoverItemButton,
} from '@koob/margaret';
import Search from 'components/Search';
import { Text } from 'ui';
import { getBedTypes } from 'api/node/bed';
import { DEFAULT_LOCAL } from 'constants';
import { useAsync } from 'react-async';

const PopoverItemButton = styled(RawPopoverItemButton)`
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};

  color: ${({ theme }) => theme.textLight};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const BedsSelectField = ({
  placeholder,
  name,
  kind,
  disabled,
  currentLocale,
}) => {
  const theme = useTheme();
  const [{ value }, , { setValue }] = useField({ name });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data } = useAsync({
    promiseFn: getBedTypes,
    filters: {
      locale: currentLocale?.value || DEFAULT_LOCAL,
      search: debouncedSearch,
    },
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const beds = (data?.data || []).map((bed) => ({
    bedId: bed.id,
    displayName: bed?.displayName || bed?.enDisplayName,
    minCapacity: bed.minCapacity,
    maxCapacity: bed.maxCapacity,
  }));

  const handleClick = (selectedBed) => {
    if (value?.length > 0) {
      if (value.some(({ bedId }) => bedId === selectedBed.bedId)) {
        setValue(value.filter(({ bedId }) => bedId !== selectedBed.bedId));
      } else {
        setValue([...value, { ...selectedBed, bedCount: 1 }]);
      }
    } else {
      setValue([{ ...selectedBed, bedCount: 1 }]);
    }
  };

  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  return (
    <FormField>
      {!disabled && (
        <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
          <Dropdown
            trigger={
              <Search
                placeholder={placeholder}
                onChange={setSearch}
                value={search}
              />
            }
          >
            <PopoverContainer>
              <PopoverMenu>
                {beds.map((bed) => (
                  <PopoverItemButton
                    key={bed.bedId}
                    onClick={() => handleClick(bed)}
                    type="button"
                  >
                    <Stack
                      size="full"
                      alignX="space-between"
                      alignY="center"
                      gutterSize={0.5}
                    >
                      <Stack alignY="center" gutterSize={0.25}>
                        <i
                          className="fa-light fa-bed"
                          style={{ fontSize: 24 }}
                        />

                        <Text type="body">
                          {bed.displayName}{' '}
                          {bed.minCapacity !== bed.maxCapacity ? (
                            <Text type="bodySmall" color="textLight">
                              ({bed.minCapacity}{' '}
                              <i className="fal fa-arrows-left-right text-gray-400" />{' '}
                              {bed.maxCapacity})
                            </Text>
                          ) : (
                            ` (${bed.minCapacity})`
                          )}
                        </Text>
                      </Stack>

                      {value.some((b) => bed.bedId === b.bedId) ? (
                        <i
                          className="fa-regular fa-circle-check"
                          style={{ fontSize: 24, color: theme.primary }}
                        />
                      ) : (
                        <i
                          className="fa-regular fa-circle-plus"
                          style={{ fontSize: 24, color: theme.textLight }}
                        />
                      )}
                    </Stack>
                  </PopoverItemButton>
                ))}
              </PopoverMenu>
            </PopoverContainer>
          </Dropdown>
        </Stack>
      )}
    </FormField>
  );
};

export default BedsSelectField;
